// Budget vs. actual for each client over the last 7, 14 and 30 days, red/yellow/green (Justin, 8 Oct 2026;
// public.dashboard_budget_pacing, rnrinfra migrations 20261008153226_budget_pacing and 20261008153810_budget_pacing_owner_now).
// Budget: the client's plan for the month and channel on the Clients page when there is one, else the daily budgets set
// in Google and Meta for its linked campaigns, recorded daily from 8 Oct. Admins and super admins only; campaigns
// linked to no client come back for super admins only.

export type PaceStatus = 'green' | 'yellow' | 'red' | 'none';
export type PaceWindow = { days: number; budget: number; actual: number; pct: number | null; status: PaceStatus; estimated: boolean };
export type PaceChannel = {
  channel: 'google_ads' | 'meta_ads'; daily_budget: number | null; campaigns: number;
  /** plan: the Clients page plan; platform: the budgets set in Google or Meta; mixed: both in the window. */
  source: 'plan' | 'platform' | 'mixed' | null; windows: PaceWindow[];
};
export type PaceClient = { business_id: string; name: string; agency_name: string; channels: PaceChannel[]; total: PaceWindow[] };
export type PaceCampaign = { platform: 'Google Ads' | 'Meta Ads'; account_id: string; campaign_id: string; name: string; daily_budget: number | null; windows: PaceWindow[] };
export type BudgetPacing = {
  as_of: string; windows: number[]; budgets_recorded_from: string | null;
  thresholds: { green: [number, number]; yellow: [number, number] };
  clients: PaceClient[];
  /** null for admins: spend linked to no client belongs to no agency. */
  unassigned: PaceCampaign[] | null;
};

export const WINDOWS = [7, 14, 30] as const;
const rank: Record<PaceStatus, number> = { red: 0, yellow: 1, green: 2, none: 3 };

export const statusLabel: Record<PaceStatus, string> = { red: 'Off budget', yellow: 'Watch', green: 'On budget', none: 'No budget' };
export const channelLabel = (channel: string) => (channel === 'google_ads' ? 'Google' : channel === 'meta_ads' ? 'Meta' : channel);
export const sourceLabel = (source: PaceChannel['source']) =>
  source === 'plan' ? 'plan' : source === 'platform' ? 'set in ads' : source === 'mixed' ? 'plan and ads' : 'no budget';

/** The window of that length; an empty one when the database has none. */
export const windowOf = (rows: PaceWindow[], days: number): PaceWindow =>
  rows.find(w => w.days === days) ?? { days, budget: 0, actual: 0, pct: null, status: 'none', estimated: false };

/** The worst status across windows: red, then yellow, then green; none only when every window has none. */
export const worst = (rows: PaceWindow[]) => rows.reduce<PaceStatus>((w, r) => (rank[r.status] < rank[w] ? r.status : w), 'none');

/** The worst 7-day pace first, then 14 and 30 days, then by name. */
export function sortByPace<T extends { name: string }>(rows: T[], windows: (row: T) => PaceWindow[]) {
  return [...rows].sort((a, b) => {
    for (const days of WINDOWS) {
      const d = rank[windowOf(windows(a), days).status] - rank[windowOf(windows(b), days).status];
      if (d) return d;
    }
    return a.name.localeCompare(b.name);
  });
}

/** How many clients are red, yellow, green or without a budget over the given window. */
export function tally(clients: PaceClient[], days: number) {
  const out: Record<PaceStatus, number> = { red: 0, yellow: 0, green: 0, none: 0 };
  for (const c of clients) out[windowOf(c.total, days).status]++;
  return out;
}

export const filterByStatus = (clients: PaceClient[], status: PaceStatus | 'all', days: number) =>
  status === 'all' ? clients : clients.filter(c => windowOf(c.total, days).status === status);

/** What the client is set to spend a day now, across channels; null when nothing is known. */
export function dailyBudget(client: PaceClient) {
  const known = client.channels.map(c => c.daily_budget).filter((n): n is number => n !== null);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
}

/** "Google: set in ads · Meta: plan": where each channel's budget comes from. */
export const sources = (client: PaceClient) => client.channels.map(c => `${channelLabel(c.channel)}: ${sourceLabel(c.source)}`).join(' · ');
